import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '../session';
import { ApiError } from '../api';
import { safePath } from '../McperApp';
import { Wordmark } from '../McperShell';
import { mcperService, type McperProviders } from '../mcper.service';
import { McperRoute } from '../routes';
import { t } from '../i18n.en';

/** Sign in to mcper.io: the provider buttons, and in development a plain email box. */
export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safePath(params.get('redirect'));
  const token = useSession((s) => s.accessToken);
  const setSession = useSession((s) => s.setSession);
  const [info, setInfo] = useState<McperProviders | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState<string | null>(null);

  useEffect(() => {
    if (token) navigate(next, { replace: true });
  }, [token, navigate, next]);

  // Opened by an agent asking for the address (redirect=/authorize?req=…): say who is waiting, before sign-in.
  useEffect(() => {
    const m = /^\/authorize\?(?:.*&)?req=([^&]+)/.exec(next);
    if (!m) return;
    mcperService.waiting(decodeURIComponent(m[1]!)).then((w) => setWaiting(w?.agent ?? null)).catch(() => undefined);
  }, [next]);

  useEffect(() => {
    mcperService
      .providers()
      .then(setInfo)
      .catch((err: ApiError) => setError(err.message));
  }, []);

  const submitLocal = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await mcperService.localLogin(email.trim());
      setSession(r.accessToken, r.person);
      navigate(next, { replace: true });
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const providers = info?.providers ?? [];
  const sso = providers.filter((p) => p !== 'local');
  const local = providers.includes('local') && info?.localLoginEnabled !== false;
  const redirect = next === McperRoute.Overview ? undefined : next;

  return (
    <div className="mio-login">
      <div className="mio-login-card">
        <a href="https://mcper.io" aria-label={t.login.home} style={{ alignSelf: 'flex-start', textDecoration: 'none' }}>
          <Wordmark tone="light" />
        </a>
        <div>
          <h1>{t.login.title}</h1>
          <span className="mio-note" style={{ display: 'block', marginTop: 6, fontSize: 14 }}>{t.login.subtitle}</span>
        </div>
        {waiting && <div className="mio-waiting">{t.login.waiting(waiting)}</div>}
        {error && <div className="mio-error">{error}</div>}
        {!info && !error && <span className="mio-note">{t.login.loading}</span>}
        {sso.map((p) => (
          <button key={p} type="button" className={`mio-btn${p === sso[0] ? '' : ' quiet'}`} onClick={() => mcperService.startSso(redirect, providers.length > 1 ? p : undefined)}>
            {t.login.continueWith(t.login.providerName[p] ?? p)}
          </button>
        ))}
        {local && (
          <form onSubmit={submitLocal}>
            <label htmlFor="mio-email">{t.login.emailLabel}</label>
            <input id="mio-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.login.emailPlaceholder} required autoFocus />
            <button type="submit" className={`mio-btn${sso.length ? ' quiet' : ''}`} disabled={busy}>{t.login.localButton}</button>
            <span className="mio-note">{t.login.localHint}</span>
          </form>
        )}
        {info && <span className="mio-note">{t.login.anyAccount}</span>}
      </div>
    </div>
  );
}
